import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import feedbackService from '@services/feedback.service';
import FeedbackModal from '@components/common/FeedbackModal';

const FeedbackContext = createContext();

export const useFeedback = () => {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used within FeedbackProvider');
  }
  return context;
};

export const FeedbackProvider = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const { user, isAuthenticated } = useSelector((state) => state.auth);

  const checkEligibility = useCallback(async () => {
    if (!isAuthenticated || !user) return;

    try {
      const response = await feedbackService.checkEligibility();
      if (response.success) {
        setEligibility(response.data);
      }
    } catch (error) {
      console.error('Error checking feedback eligibility:', error);
    }
  }, [isAuthenticated, user]);

  // Run only once when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      checkEligibility();
    }
  }, [isAuthenticated, user?.id]); // Only re-run if auth status or user ID changes

  const showFeedbackModal = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await feedbackService.checkEligibility();
      if (response.success && response.data?.shouldShow) {
        await feedbackService.recordShown();
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error('Error showing feedback modal:', error);
    }
  }, [isAuthenticated]);

  const forceShowFeedbackModal = useCallback(() => {
    if (!isAuthenticated) return;
    setIsModalOpen(true);
  }, [isAuthenticated]);

  const triggerFeedbackOnLogout = useCallback(() => {
    if (eligibility?.shouldShow) {
      showFeedbackModal();
    }
  }, [eligibility, showFeedbackModal]);

  const closeFeedbackModal = () => {
    setIsModalOpen(false);
  };

  const handleFeedbackSubmitted = () => {
    setEligibility({ shouldShow: false, reason: 'already_submitted' });
  };

  const value = {
    showFeedbackModal,
    forceShowFeedbackModal,
    triggerFeedbackOnLogout,
    eligibility,
    checkEligibility
  };

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <FeedbackModal
        isOpen={isModalOpen}
        onClose={closeFeedbackModal}
        onSubmitSuccess={handleFeedbackSubmitted}
      />
    </FeedbackContext.Provider>
  );
};
